/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AlertData } from '../../../alert/shared/model/alert-data.model';
import { Alert } from '../../../alert/shared/model/alert.model';
import { AlertServiceService } from '../../../alert/shared/service/alert-service.service';
import { LmsLoaderService } from '../../../core/services/lms-loader.service';
import { PopupService } from '../../../core/services/popup.service';
import { AuthenticatedUser } from '../../model/authenticated-user.model';
import { MessageDetails } from '../../model/message-details.model';
import { Tab } from '../../model/tab.model';
import { UserProfile } from '../../model/user-profile.model';
import { AppUtilityService } from '../../services/app-utility.service';
import { ConfigService } from '../../services/config.service';
import { DataStoreService } from '../../services/datastore.service';
import { LmsLoggerService } from '../../services/lms-logger.service';
import { LmsModuleService } from '../../services/lms-module.service';
import { LmsRendererService } from '../../services/lms-renderer.service';
import { StaticDataStoreService } from '../../services/static-datastore.service';
import { SystemInitService } from '../../services/system-init.service';
import { TabService } from '../../services/tab.service';
import { UserService } from '../../services/user.service';
import { UtilService } from '../../services/util.service';
import { MessageHandlerService } from './../../services/message-handler.service';
import { UserProfileService } from './../../services/user-profile.service';


/**
 * @export
 * @class LmsUserInfoComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'lms-user-info',
  templateUrl: './lms-user-info.component.html',
  styleUrls: ['./lms-user-info.component.scss']
})
export class LmsUserInfoComponent implements OnInit, OnDestroy {

  user: AuthenticatedUser;
  subscription: any;
  tabLevel: number;
  bellIconPath: string;
  isShowUserProfile: boolean;
  initWindowCount = 5;
  alertCount = 0;
  popupErrorMessage: string;
  errorDialog = false;
  configData: any;
  userProfileData: UserProfile;
  alertSubscription: any;
  selectedTabSubscription: any;
  userPrefSubscription: any;
  alertsInfo: AlertData;
  alerts: Array<Alert>;
  lastAccessedTime: string;
  selectedTab: Tab;
  showWarningIcon = false;

  /**
   * Creates an instance of LmsUserInfoComponent.
   * @param {UserService} userService
   * @param {UtilService} utilService
   * @param {ConfigService} configService
   * @memberOf LmsUserInfoComponent
   */
  constructor(private userService: UserService,
    private utilService: UtilService,
    private configService: ConfigService,
    private popupService: PopupService,
    private router: Router,
    private userProfileService: UserProfileService,
    private lmsLoaderService: LmsLoaderService,
    private staticDataStoreService: StaticDataStoreService,
    private messageHandlerService: MessageHandlerService,
    private alertsService: AlertServiceService,
    private appUtilService: AppUtilityService,
    private lmsModuleService: LmsModuleService,
    private systemInitService: SystemInitService,
    private lmsRendererService: LmsRendererService, private lmsLoggerService: LmsLoggerService, private tabService: TabService, private dataStoreService: DataStoreService) {
    this.configData = this.configService.getConfigData();
    this.tabLevel = this.configData.tabIndex.userInfo;
    this.bellIconPath = this.configService.getConfigData().images.bellIcon;
    this.lmsLoaderService.showLoader();
    this.selectedTabSubscription = this.lmsModuleService.selectedTab.subscribe((data) => {
      this.selectedTab = data;
    });
    this.alertSubscription = this.alertsService.alerts.subscribe((alerts) => {
      this.alerts = [];
      if (alerts && alerts.httpStatus && alerts.httpStatus === 200) {
        if (this.selectedTab && this.selectedTab.type !== 'Alerts') {
          this.alertsInfo = alerts;
          this.lastAccessedTime = alerts.lastAccessedTime;
          this.initializeAlerts();
          this.lmsLoaderService.hideLoader();
        }
        this.showWarningIcon = false;
      } else if (alerts.httpStatus && alerts.httpStatus === 500) {
        this.showWarningIcon = true;
      }
    });
    this.userPrefSubscription = this.systemInitService.userPrefLoad.subscribe((loaded) => {
      if (loaded === true) {
        this.userProfileData = this.staticDataStoreService.getValue('userProfile');
      }
      if (!this.userProfileData || !this.userProfileData.alertType) {
        setTimeout(() => {
          this.userProfileData = this.staticDataStoreService.getValue('userProfile');
        }, 1000);
      }
    });
  }

  /**
   * Initialize the state lifecycle hook.
   * @memberOf LmsUserInfoComponent
   */
  ngOnInit() {
    this.popupErrorMessage = 'User Profile';
    this.errorDialog = false;
    this.subscription = this.userService.userInfo.subscribe(data => {
      if (!this.utilService.isEmptyObject(data)) {
        this.user = data;
      }
    });
  }

  /**
   * Initialize alerts.
   * @memberof LmsUserInfoComponent
   */
  initializeAlerts() {
    if (this.alertsInfo && this.alertsInfo.alerts) {
      this.alerts = this.alertsInfo.alerts;
      this.calculateAlertCountFromAlerts();
      if (this.alerts && this.alerts.length > 1) {
        this.alerts = this.sortAlertByCreationDate(this.alerts);
      }
    }
  }

  /**
   * Calculate alert count from all alerts.
   * @private
   * @memberof LmsUserInfoComponent
   */
  private calculateAlertCountFromAlerts() {
    this.alertCount = 0;
    if (this.alerts && this.alerts.length > 0) {
      this.alerts = this.alerts.filter((alert) => {
        if (this.userProfileData && this.userProfileData.alertType) {
          if (this.userProfileData.alertType.toUpperCase() === 'SYS') {
            if (alert.priority === '1' && !alert.dirSignOffTs) {
              this.alertCount++;
            }
          }
          if (this.userProfileData.alertType.toUpperCase() === 'DES') {
            if (alert.priority === '1' && !alert.deskSignOffTs) {
              this.alertCount++;
            }
          }
          if (alert.priority === '1' && !alert.isAlertRead && this.notSignedOff(alert)) {
            return true;
          } else {
            return false;
          }
        }
      });
    }
  }

  private notSignedOff(alert: Alert) {
    if (this.userProfileData.alertType.toUpperCase() === 'SYS') {
      if (alert.priority === '1' && !alert.dirSignOffTs) {
        return true;
      } else {
        return false;
      }
    } else if (this.userProfileData.alertType.toUpperCase() === 'DES') {
      if (alert.priority === '1' && !alert.deskSignOffTs) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  private sortAlertByCreationDate(alerts: Array<Alert>) {
    return alerts.sort((alert1, alert2) => {
      if (alert1.priority === alert2.priority) {
        if (this.getDateFromString(alert1.creationTs) < this.getDateFromString(alert2.creationTs)) {
          return 1;
        } else if (this.getDateFromString(alert1.creationTs) > this.getDateFromString(alert2.creationTs)) {
          return -1;
        } else if (this.getDateFromString(alert1.creationTs) === this.getDateFromString(alert2.creationTs)) {
          return 0;
        }
      }
    });
  }

  getDateFromString(date: string): Date {
    const dateObj = new Date(date);
    return dateObj;
  }

  /**
   * Invoked for closing of alert.
   * @param {Alert} alert
   * @memberof LmsUserInfoComponent
   */
  public closeThisAlert(alert: Alert) {
    this.alertsService.alertReadUpdate(alert).subscribe(data => {
      const alertTab = this.tabService.getTabDataBySubType('Alerts', 'data')[0];
      const alertTabData = this.dataStoreService.getValue(alertTab);
      for (const alertData of alertTabData.data.Data['alerts']) {
        if (alert.alertSeq === alertData.alertSeq) {
          alertData.isAlertRead = true;
        }
      }
      this.dataStoreService.putValue(alertTab, alertTabData);
      this.alertsService.updateAlertsAfterRead(<AlertData>alertTabData.data.Data);
      this.alerts = this.alerts.filter((alertTemp) => {
        return alertTemp.alertSeq !== alert.alertSeq;
      });
    }, error => {
      this.lmsLoggerService.logError('Error In updating Alert');
    });
  }

  /**
   * this is used to set focus back to Start
   * @memberOf LmsUserInfoComponent
   */
  public setFocusBacktoStart() {
    const firstFocus = document.getElementById('menuItem' + this.configData.tabIndex.sideNavigation);
    if (firstFocus) {
      firstFocus.focus();
    }
  }

  /**
   * Destroy the lifecycle hook.
   * @memberOf LmsUserInfoComponent
   */
  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.selectedTabSubscription.unsubscribe();
    this.userPrefSubscription.unsubscribe();
  }

  /**
   * Show user profile popup.
   * @memberOf LmsUserInfoComponent
   */
  showUserProfilePopup() {
    this.lmsLoaderService.showLoader();
    this.userProfileService.getData().subscribe(userProfileData => {
      this.lmsLoaderService.hideLoader();
      this.userProfileService.load(userProfileData);
      const userProfile = {
        operation: 'userProfilePop'
      };
      this.popupService.setPopupOperation(userProfile);
    }, error => {
      this.lmsLoaderService.hideLoader();
      this.messageHandlerService.handleMessage(<MessageDetails>({
        type: this.configService.getConfigData().message.type.server,
        level: this.configService.getConfigData().message.level.error,
        target: this.configService.getConfigData().message.target.page,
        message: this.appUtilService.getHttpErroMessage(error, false)
      }));
    });
  }

  /**
   * Reser page mode.
   * @memberOf LmsUserInfoComponent
   */
  resetPageMode() {
    this.popupService.setPageMode('');
    this.popupService.setSubmitBtnId('');
  }

  /**
   * Invoked for close user profile popup.
   * @param {any} event
   * @memberOf LmsUserInfoComponent
   */
  cancelUserProfilePopup(event) {
    if (event) {
      this.isShowUserProfile = false;
    }
  }

  /**
   * Set error message.
   * @param {any} event
   * @memberOf LmsUserInfoComponent
   */
  displayPopupError(event) {
    this.popupErrorMessage = event;
  }

  /**
   * Invoked for show error message in dialog
   * @param {any} event
   * @memberOf LmsUserInfoComponent
   */
  isOpenErrorDialog(event) {
    this.errorDialog = event;
  }

  /**
   * Update last access time.
   * @private
   * @memberof LmsUserInfoComponent
   */
  private updateLastAccessedTime() {
    const currentDateTime = new Date();
    this.lastAccessedTime = `${this.appUtilService.leftPad((currentDateTime.getMonth() + 1), 0, 2)}/${currentDateTime.getDate()}/${currentDateTime.getFullYear()} ${currentDateTime.getHours()}:${currentDateTime.getMinutes()}`;
  }

  /**
   * Navigate to alert window when click on notification icon.
   * @memberof LmsUserInfoComponent
   */
  routeToAlertWindow() {
    this.alerts = [];
    this.updateLastAccessedTime();
    this.lmsRendererService.setLastUrl(this.selectedTab);
    const tab = this.tabService.getTabDataBySubType('Alerts', 'Data')[0];
    if (tab) {
      this.appUtilService.navigateByUrl('alerts', 'data', '', true, true, tab.id);
    } else {
      this.appUtilService.navigateByUrl('alerts', 'data', '', true);
    }
  }
}
